import { api } from "./api/client.js";
import {
  forgetActiveClientId,
  readActiveClientId,
  writeActiveClientId,
} from "./activeClientStorage.js";
import { withRetry } from "@bitcashier/shared/core/retry.js";
import { isReauthError } from "@bitcashier/shared/auth/reauth.js";
import { reportError } from "./observability/sentry.js";

// Port of the clients/activeClient half of src/user/modules/layout_elements/layout.jsx plus
// store/actions/{clients,active_client}. The active client id lives in the `active-client` cookie
// (or, while viewing the dashboard as another user, in this tab alone — see activeClientStorage.js)
// because the API transport reads it there for the X-Client-Id header on every request.
//
// Unlike the admin panel (where the active client is an incidental default), a user account can
// legitimately hold several clients and switches between them from the header — so this store
// owns the switch and the re-fetch it triggers.

export const clients = $state({
  requested: false,
  loaded: false,
  items: [],
  activeId: readActiveClientId(),
  error: null,
});

export const activeClient = () => clients.items.find((client) => client.id === clients.activeId);

export async function loadClients() {
  clients.requested = true;
  try {
    clients.items = await fetchClients();
    clients.error = null;

    // Keep the cookie's client if it is still one of ours; otherwise fall back to the first, which
    // is what React did for a first-time user with no cookie.
    const stored = clients.items.find((client) => client.id === clients.activeId);
    const resolved = stored ?? clients.items[0];
    if (resolved && resolved.id !== clients.activeId) {
      clients.activeId = resolved.id;
      writeActiveClientId(resolved.id);
    }
  } catch (e) {
    clients.items = [];
    // A dead session is neither this screen's failure nor a boot failure: getToken() has already
    // sent the user back through Auth0, so leaving `error` unset keeps the loader up for the
    // moment the browser takes to navigate rather than flashing "could not load your clients" at
    // somebody who is only being signed in again — and keeps the report below meaning what it says.
    if (isReauthError(e)) return;
    clients.error = e;
    // This failure is the whole screen — the user gets "could not load your clients" and nothing
    // else — and it was reported nowhere: caught here, it never reached Sentry's window listeners,
    // so how often it happens and what answered could only be guessed at from the odd complaint.
    reportError(e, { boot: "clients", status: e?.status ?? null });
  } finally {
    clients.loaded = true;
  }
}

// The active client is sent as X-Client-Id on EVERY request, and the gateway rejects the whole
// request when that client is blocked, deleted or no longer ours ("Selected client is blocked" and
// friends, Auth0TokenAuthenticator). That includes the clients call — the one whose job is to
// replace the stale id — so a bad cookie locked the dashboard out for good: nothing on screen could
// load, and nothing cleared the cookie. On a 401 we therefore drop the cookie and ask once more
// without it, which the gateway accepts (a missing header means "no client selected").
//
// Exported because the boot fires this and the permissions read in PARALLEL (App.svelte), so the
// permissions request goes out carrying the same stale id and is rejected the same way. Recovering
// in only one of them left the other's failure permanent: loadPermissions swallows its error and
// still sets `loaded`, so the second boot effect never re-fires and the user lands on a dashboard
// where every scope check answers false, with nothing on screen saying why (DASHBOARD-BZ).
//
// `sentWith` is what the request actually carried, which is what the race needs: a 401 on a request
// that carried no id is about something else and must not clear anything, and if the parallel call
// has meanwhile replaced the id with a good one there is nothing stale left to drop — retrying with
// it is right.
export async function withoutStaleClient(request) {
  const sentWith = clients.activeId;
  try {
    return await request();
  } catch (e) {
    if (e?.status !== 401 || !sentWith) throw e;
    if (clients.activeId === sentWith) {
      clients.activeId = null;
      forgetActiveClientId();
    }
    return await request();
  }
}

const fetchClients = () => withoutStaleClient(getClients);

// This is the app's first request and nothing renders until it answers, so a blip that any second
// attempt would have survived shows the user a dead end instead of the dashboard. `api` deploys one
// instance at a time, and a request crossing that handover is answered by the edge (502/503) rather
// than by us — which is why the report is "it happens right after a deployment, once".
const getClients = async () => (await withRetry(() => api.get("/ui/clients")))?.items ?? [];

// Switching client changes the X-Client-Id every subsequent request carries, so everything on
// screen — balances, permissions, every table — is stale at once. React re-fetched permissions and
// let each screen's effects re-run; reloading gets the same result without threading a refresh
// signal through every screen, and guarantees no stale data survives the switch.
export function switchClient(clientId) {
  if (clientId === clients.activeId) return;
  clients.activeId = clientId;
  writeActiveClientId(clientId);
  window.location.reload();
}
